// src/components/GiornaleCantiere.js
import React, { useState, useEffect, useRef } from "react";
import {
  collection, doc, onSnapshot, setDoc, updateDoc, addDoc,
  query, where, getDocs, serverTimestamp, getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";
import { Plus, FileText, Printer, Edit2, Check, X, ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import GiornaleVistaMensile from "./GiornaleVistaMensile";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";
import FirmaPad, { FirmaImmagine } from "./FirmaPad";
import { descrizioneSede } from "../utils/sediCliente";
import { chiaveBozza, prefissoBozze, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza, { ElencoBozze, delGiorno } from "./AvvisoBozza";
import { UsaMiaFirma } from "./MiaFirma";
import DittePresentiGiornale from "./DittePresentiGiornale";
import { useIdoneitaDitte } from "./IdoneitaDitte";
import { dittePresenti } from "../utils/ditteEsterne";

// Bozza automatica del giorno aperto (utils/bozzeLocali.js): se il telefono chiude la
// pagina a metà compilazione, il giorno si ritrova com'era. Si monta con il modulo del giorno.
function BozzaGiorno({ uid, projectId, nomeCommessa, giorno, formGiorno, setFormGiorno, riprendi, bozzaRef }) {
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(uid, "giornale", projectId, giorno?.id),
    valore: formGiorno,
    info: { data: formGiorno?.data, titolo: `Giornale di cantiere — ${giorno ? "giorno" : "nuovo giorno"}${delGiorno(formGiorno?.data)}${nomeCommessa ? ` — ${nomeCommessa}` : ""}`,
      vai: { page: "project", projectId, initialTab: "giornale" } },
    applica: d => setFormGiorno(f => (f ? { ...f, ...d } : f)),
    riprendiSubito: riprendi,
  });
  bozzaRef.current = bozza;
  if (!bozza.trovata) return null;
  return <AvvisoBozza testo={giorno ? "Hai modifiche non salvate a questo giorno" : "Hai un giorno del giornale iniziato e non salvato"}
    salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />;
}
// testo inserito nella stampa: i caratteri speciali non devono rompere la pagina
const testoHtml = (t) => String(t ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const METEO_OPTIONS = ["☀️ Sole","⛅ Parzialmente nuvoloso","☁️ Nuvoloso","🌧️ Pioggia","⛈️ Temporale","🌨️ Neve","💨 Vento forte","🌫️ Nebbia"];

const inp = { width:"100%", padding:"8px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", boxSizing:"border-box", outline:"none" };
const lbl = { fontSize:11, fontWeight:700, color:"#6b7280", display:"block", marginBottom:4 };
const cardStyle = { background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, padding:20, marginBottom:16 };

// Componente firma digitale
// Firma del giornale: si mostra quella salvata; «Firma» apre il componente unico di firma
// (components/FirmaPad.js), a tutto schermo sul telefono.
function FirmaDigitale({ label, value, onChange, conMiaFirma = false }) {
  const [aperta, setAperta] = useState(false);
  // Firma precedente: se si chiude il riquadro senza salvare torna quella. Mentre si firma,
  // ogni tratto aggiorna la bozza del giorno: salvando la giornata la firma non va persa.
  const [prima, setPrima] = useState(null);
  const apri = () => { setPrima(value || null); setAperta(true); };
  return (
    <div>
      <label style={lbl}>{label}</label>
      {value
        ? <div style={{ border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", padding:8, display:"flex", alignItems:"center", gap:10 }}>
            <FirmaImmagine src={value} alt="Firma" style={{ maxHeight:80, maxWidth:"60%" }}/>
            <span style={{ flex:1 }}/>
            <button type="button" onClick={apri} style={{ fontSize:11, color:"#7c3aed", background:"none", border:"none", cursor:"pointer" }}>Rifai</button>
            <button type="button" onClick={()=>onChange(null)} style={{ fontSize:11, color:"#dc2626", background:"none", border:"none", cursor:"pointer" }}>× Cancella</button>
          </div>
        : <button type="button" onClick={apri} style={{ width:"100%", padding:"18px", border:"1px dashed #d1d5db", borderRadius:8, background:"#fafafa", color:"#6b7280", fontSize:13, cursor:"pointer" }}>✍️ Firma</button>}
      {/* firma salvata (components/MiaFirma.js): per chi firma come responsabile */}
      {!value && !aperta && conMiaFirma && <div style={{ marginTop:6 }}><UsaMiaFirma onUsa={onChange} /></div>}
      {aperta && <div style={{ marginTop:8 }}><FirmaPad label={label} onSave={d=>{ onChange(d); setAperta(false); }}
        onBozza={d=>{ if (d) onChange(d); }} onCancel={()=>{ onChange(prima); setAperta(false); }}/></div>}
    </div>
  );
}

// Sul telefono (≤ 768 px): prima l'elenco dei giorni a tutta larghezza, poi il giorno aperto a
// tutta larghezza con «← Giorni»; le griglie diventano a una colonna. La radice è l'unico
// riquadro che scorre (il contenitore della commessa taglia ciò che sporge: W415).
// Sul computer non cambia nulla.
const CSS_TELEFONO = `
@media (max-width: 768px) {
  .gc-root { display: block !important; height: 100% !important; overflow-y: auto !important; -webkit-overflow-scrolling: touch; }
  .gc-lista { width: 100% !important; border-right: none !important; border-bottom: 1px solid #e5e7eb; }
  .gc-root.gc-aperto .gc-lista, .gc-root.gc-aperto .gc-dati { display: none !important; }
  .gc-main { padding: 12px !important; overflow: visible !important; }
  .gc-lista > div { overflow: visible !important; }
  .gc-griglia { grid-template-columns: 1fr !important; }
  .gc-griglia2 { grid-template-columns: 1fr 1fr !important; }
  .gc-testata { flex-wrap: wrap; gap: 8px; }
  .gc-riga { flex-wrap: wrap; }
  .gc-riga > select { flex: 1 1 100% !important; }
  .gc-riga > input[type=text] { flex: 1 1 auto; width: auto !important; min-width: 0; }
  .gc-solo-tel { display: inline-flex !important; }
  .gc-card { padding: 14px !important; }
}
`;

export default function GiornaleCantiere({ projectId, orgId, project, isAdmin, currentUser }) {
  // Commessa chiusa = sola consultazione. Riapri la commessa per aggiungere/modificare giorni.
  const commessaChiusa = project?.statoCommessa === "chiusa";
  const { addToast } = useToast();
  // ditte esterne con documenti scaduti o mancanti (piano Gold, W413)
  const idoneita = useIdoneitaDitte(orgId);
  const [datiCommessa, setDatiCommessa] = useState(null);
  const [editDati, setEditDati] = useState(false);
  const [vistaMensileAttiva, setVistaMensileAttiva] = useState(false);
  const [formDati, setFormDati] = useState({
    appaltante_nome:"", appaltante_indirizzo:"", appaltante_piva:"", appaltante_referente:"",
    appaltatore_nome:"", appaltatore_indirizzo:"", appaltatore_piva:"", subappaltatrice_nome:"",
    dl_nome:"", dl_albo:"", dl_recapito:"",
    progetto_esecutivo_nome:"", cse_nome:"", rup_nome:"",
    oggetto:"", importo:"", dataConsegna: project?.dataFine||"",
  });
  const [giorni, setGiorni] = useState([]);
  const [giornoSelezionato, setGiornoSelezionato] = useState(null);
  const [formGiorno, setFormGiorno] = useState(null);
  const [presenzeGiorno, setPresenzeGiorno] = useState([]);
  const [rapportiniGiorno, setRapportiniGiorno] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [showNuovoGiorno, setShowNuovoGiorno] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [riprendiBozzaGiorno, setRiprendiBozzaGiorno] = useState(false);
  const [aperturaGiorno, setAperturaGiorno] = useState(0); // ogni apertura ricontrolla la bozza
  const bozzaGiornoRef = useRef(null);
  // sul telefono, aprendo un giorno (o la vista mensile) il modulo prende il posto dell'elenco: si torna in cima
  const radiceRef = useRef(null);
  const chiaveAperta = formGiorno ? (giornoSelezionato?.id || "nuovo") : vistaMensileAttiva ? "mensile" : "";
  useEffect(() => {
    if (!chiaveAperta || !radiceRef.current || typeof window === "undefined" || !window.matchMedia?.("(max-width: 768px)").matches) return;
    radiceRef.current.scrollTop = 0; // sul telefono il giornale scorre dentro la sua radice
  }, [chiaveAperta]);

  const oggi = toLocalDateStr(new Date());

  // Carica dati organizzazione
  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap => { if(snap.exists()) setOrgInfo(snap.data()); });
  }, [orgId]);

  // Carica dati commessa
  useEffect(() => {
    if (!projectId) return;
    return onSnapshot(doc(db,"giornaleCantiere",projectId), snap => {
      if (snap.exists()) {
        setDatiCommessa(snap.data());
        setFormDati(f => ({ ...f, ...snap.data() }));
      }
    });
  }, [projectId]);

  // Carica giorni
  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(
      query(collection(db,"giornaleCantiereDays"), where("projectId","==",projectId), where("orgId","==",orgId)),
      snap => {
        const list = snap.docs.map(d=>({id:d.id,...d.data()}))
          .sort((a,b)=>b.data.localeCompare(a.data));
        setGiorni(list);
      }
    );
  }, [projectId, orgId]);

  // Carica dipendenti
  useEffect(() => {
    if (!orgId) return;
    getDocs(query(collection(db,"dipendenti"), where("organizationId","==",orgId)))
      .then(snap => setDipendenti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [orgId]);

  const salvaDatiCommessa = async () => {
    await setDoc(doc(db,"giornaleCantiere",projectId), {
      ...formDati, projectId, orgId, updatedAt: serverTimestamp()
    });
    setEditDati(false);
  };

  const apriGiorno = async (giorno) => {
    setVistaMensileAttiva(false);
    setRiprendiBozzaGiorno(false);
    setAperturaGiorno(n => n + 1);
    setGiornoSelezionato(giorno);
    setFormGiorno({ ...giorno });
    // Carica presenze del giorno
    const dataG = giorno.data;
    const anno = parseInt(dataG.split("-")[0]);
    const mese = parseInt(dataG.split("-")[1]);
    const giornoNum = parseInt(dataG.split("-")[2]);
    const presSnap = await getDocs(query(collection(db,"presenze"),
      where("orgId","==",orgId), where("anno","==",anno), where("mese","==",mese)));
    const presenti = [];
    presSnap.docs.forEach(d => {
      const data = d.data();
      const g = data.giorni?.[giornoNum];
      if (g?.timbrature?.length > 0) {
        const dip = dipendenti.find(x=>x.nexoraUid===data.uid);
        if (dip) presenti.push(`${dip.nome||""} ${dip.cognome||""}`.trim());
      }
    });
    setPresenzeGiorno(presenti);
    // Carica rapportini del giorno
    const rapSnap = await getDocs(query(collection(db,"rapportini"),
      where("projectId","==",projectId), where("data","==",dataG), where("orgId","==",orgId)));
    setRapportiniGiorno(rapSnap.docs.map(d=>d.data()));
  };

  const nuovoGiorno = (dataSpecifica) => {
    setVistaMensileAttiva(false);
    setRiprendiBozzaGiorno(false);
    setAperturaGiorno(n => n + 1);
    setFormGiorno({
      data: dataSpecifica || oggi, meteo:"☀️ Sole", temperatura:"", lavorieseguiti:"",
      materialiRicevuti:"", annotazioni:"", visiteIspettive:"", sospensioni:"",
      attrezzature:"", subappaltatori:"", ditteEsterne:[], manodopera:[], criticita:[], responsabile: currentUser?.displayName||"",
      firma: null, projectId, orgId
    });
    setShowNuovoGiorno(true);
    setGiornoSelezionato(null);
  };

  const apriOCreaGiornoData = (dateObj) => {
    const ds = `${dateObj.getFullYear()}-${String(dateObj.getMonth()+1).padStart(2,"0")}-${String(dateObj.getDate()).padStart(2,"0")}`;
    const esistente = giorni.find(g=>g.data===ds);
    if (esistente) apriGiorno(esistente);
    else nuovoGiorno(ds);
  };

  const salvaGiorno = async () => {
    if (commessaChiusa || !formGiorno?.data) return;
    setLoading(true);
    const existing = giorni.find(g => g.data === formGiorno.data);
    if (existing) {
      await updateDoc(doc(db,"giornaleCantiereDays",existing.id), { ...formGiorno, updatedAt: serverTimestamp() });
    } else {
      await addDoc(collection(db,"giornaleCantiereDays"), { ...formGiorno, projectId, orgId, createdAt: serverTimestamp() });
    }
    bozzaGiornoRef.current?.salvato();
    setLoading(false);
    setShowNuovoGiorno(false);
    setGiornoSelezionato(null);
    setFormGiorno(null);
  };

  const stampaPDF = (giorno) => {
    const dati = datiCommessa || {};
    const win = window.open("", "_blank");
    const dataFmt = new Date(giorno.data+"T00:00:00").toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
    const numGiorno = giorni.filter(g=>g.data<=giorno.data).length;
    const logoHtml = orgInfo?.logo ? `<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>` :
      `<h1>◆ ${orgInfo?.nome||orgInfo?.name||dati.appaltatore_nome||""}</h1>`;

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Giornale di Cantiere — ${giorno.data}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20mm; }
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      .header { border-bottom: 3px solid #1e1b4b; padding-bottom: 10px; margin-bottom: 14px; display: flex; align-items: flex-start; justify-content: space-between; }
      .logo-area h1 { font-size: 20px; color: #1e1b4b; font-weight: 900; letter-spacing: -0.5px; }
      .logo-area p { font-size: 11px; color: #666; }
      .title-area { text-align: right; }
      .title-area h2 { font-size: 15px; color: #1e1b4b; font-weight: 800; }
      .title-area p { font-size: 11px; color: #666; }
      .badge { display: inline-block; background: #1e1b4b; color: #fff; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; margin-top: 4px; }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
      .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin-bottom: 12px; }
      .box { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; }
      .box-title { font-size: 9px; font-weight: 700; color: #7c3aed; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
      .field { margin-bottom: 5px; }
      .field-label { font-size: 9px; color: #999; font-weight: 600; }
      .field-value { font-size: 11px; color: #111; font-weight: 500; min-height: 14px; }
      .section { margin-bottom: 12px; }
      .section-title { font-size: 10px; font-weight: 800; color: #1e1b4b; text-transform: uppercase; letter-spacing: 0.05em; background: #f3f4f6; padding: 5px 8px; border-radius: 4px; margin-bottom: 6px; }
      .text-area { border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px; min-height: 40px; font-size: 11px; line-height: 1.5; }
      .firma-box { border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px; min-height: 80px; display: flex; align-items: center; justify-content: center; }
      .footer { border-top: 1px solid #e5e7eb; margin-top: 14px; padding-top: 8px; font-size: 9px; color: #999; display: flex; justify-content: space-between; }
      @media print { body { padding: 10mm; } }
    </style></head><body>

    <div class="header">
      <div class="logo-area">
        ${logoHtml}
        <div style="font-size:11px;font-weight:700;color:#374151;margin-top:2px;">${orgInfo?.nome||orgInfo?.name||dati.appaltatore_nome||""}</div>
        ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280;">${orgInfo.indirizzo}</div>`:""}
        ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280;">P.IVA ${orgInfo.piva}</div>`:""}
      </div>
      <div class="title-area">
        <h2>GIORNALE DI CANTIERE</h2>
        <p>${dataFmt}</p>
        <div class="badge">Giorno n° ${numGiorno}</div>
      </div>
    </div>

    <div class="grid-3">
      <div class="box">
        <div class="box-title">Appaltante</div>
        <div class="field"><div class="field-label">Denominazione</div><div class="field-value">${dati.appaltante_nome||"—"}</div></div>
        <div class="field"><div class="field-label">Indirizzo</div><div class="field-value">${dati.appaltante_indirizzo||"—"}</div></div>
        <div class="field"><div class="field-label">P.IVA / CF</div><div class="field-value">${dati.appaltante_piva||"—"}</div></div>
        <div class="field"><div class="field-label">Referente</div><div class="field-value">${dati.appaltante_referente||"—"}</div></div>
      </div>
      <div class="box">
        <div class="box-title">Appaltatore</div>
        <div class="field"><div class="field-label">Denominazione</div><div class="field-value">${dati.appaltatore_nome||"—"}</div></div>
        <div class="field"><div class="field-label">Indirizzo</div><div class="field-value">${dati.appaltatore_indirizzo||"—"}</div></div>
        <div class="field"><div class="field-label">P.IVA</div><div class="field-value">${dati.appaltatore_piva||"—"}</div></div>
      </div>
      <div class="box">
        <div class="box-title">Direzione Lavori</div>
        <div class="field"><div class="field-label">Nominativo</div><div class="field-value">${dati.dl_nome||"—"}</div></div>
        <div class="field"><div class="field-label">Ordine / Albo</div><div class="field-value">${dati.dl_albo||"—"}</div></div>
        <div class="field"><div class="field-label">Recapito</div><div class="field-value">${dati.dl_recapito||"—"}</div></div>
      </div>
    </div>

    ${(dati.progetto_esecutivo_nome||dati.cse_nome||dati.rup_nome) ? `<div style="font-size:10px;color:#374151;margin-bottom:12px;">
      ${dati.progetto_esecutivo_nome?`<strong>Progetto Esecutivo:</strong> ${dati.progetto_esecutivo_nome} &nbsp;·&nbsp; `:""}
      ${dati.cse_nome?`<strong>CSE:</strong> ${dati.cse_nome} &nbsp;·&nbsp; `:""}
      ${dati.rup_nome?`<strong>RUP:</strong> ${dati.rup_nome}`:""}
    </div>` : ""}

    <div class="box" style="margin-bottom:12px">
      <div class="box-title">Dati commessa</div>
      <div class="grid-3" style="margin-bottom:0">
        <div class="field"><div class="field-label">Oggetto lavori</div><div class="field-value">${dati.oggetto||project?.name||"—"}</div></div>
        ${(project?.sede?.indirizzo||project?.sede?.nome)?`<div class="field"><div class="field-label">Luogo dei lavori</div><div class="field-value">${testoHtml(descrizioneSede(project.sede))}</div></div>`:""}
        <div class="field"><div class="field-label">Importo contrattuale</div><div class="field-value">${dati.importo?"€ "+dati.importo:"—"}</div></div>
        <div class="field"><div class="field-label">Data consegna</div><div class="field-value" style="color:${dati.dataConsegna&&new Date(dati.dataConsegna)<new Date()?"#dc2626":"#059669"};font-weight:700">${dati.dataConsegna?new Date(dati.dataConsegna+"T00:00:00").toLocaleDateString("it-IT"):"—"}</div></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">☁️ Condizioni meteorologiche</div>
      <div class="grid-2" style="margin-bottom:0">
        <div class="field"><div class="field-label">Meteo</div><div class="field-value">${giorno.meteo||"—"}</div></div>
        <div class="field"><div class="field-label">Temperatura</div><div class="field-value">${giorno.temperatura?""+giorno.temperatura+"°C":"—"}</div></div>
      </div>
    </div>

    ${(giorno.manodopera||[]).length>0 ? `
    <div class="section">
      <div class="section-title">⏱️ Manodopera e figure professionali — nominativo e ore lavorate</div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr style="background:#f3f4f6;">
          <th style="text-align:left;padding:5px 8px;border:1px solid #e5e7eb;">Nominativo</th>
          <th style="text-align:right;padding:5px 8px;border:1px solid #e5e7eb;width:100px;">Ore lavorate</th>
        </tr></thead>
        <tbody>
          ${giorno.manodopera.map(r=>`<tr><td style="padding:5px 8px;border:1px solid #e5e7eb;">${r.nome||"—"}</td><td style="text-align:right;padding:5px 8px;border:1px solid #e5e7eb;">${r.ore||"—"}</td></tr>`).join("")}
          <tr style="font-weight:700;background:#faf5ff;"><td style="padding:5px 8px;border:1px solid #e5e7eb;">Totale ore</td><td style="text-align:right;padding:5px 8px;border:1px solid #e5e7eb;">${giorno.manodopera.reduce((s,r)=>s+(parseFloat(r.ore)||0),0)}</td></tr>
        </tbody>
      </table>
    </div>` : ""}

    <div class="section">
      <div class="section-title">🔧 Lavori eseguiti</div>
      <div class="text-area">${giorno.lavorieseguiti || rapportiniGiorno.map(r=>r.lavorazioni||r.descrizioneBreve).join("; ") || "—"}</div>
    </div>

    <div class="grid-2">
      <div class="section">
        <div class="section-title">📦 Materiali e forniture ricevute</div>
        <div class="text-area">${giorno.materialiRicevuti||"—"}</div>
      </div>
      <div class="section">
        <div class="section-title">🚧 Attrezzature e mezzi in cantiere</div>
        <div class="text-area">${giorno.attrezzature||"—"}</div>
      </div>
    </div>

    ${dittePresenti(giorno).length || giorno.subappaltatori ? `<div class="section"><div class="section-title">🤝 Ditte esterne presenti</div><div class="text-area">${
      dittePresenti(giorno).map(x => `<b>${testoHtml(x.nome)}</b>${x.uomini ? ` — ${x.uomini} uomin${x.uomini === 1 ? "o" : "i"}` : ""}${x.lavorazioni ? `: ${testoHtml(x.lavorazioni)}` : ""}`).join("<br/>")
    }${dittePresenti(giorno).length && giorno.subappaltatori ? "<br/>" : ""}${giorno.subappaltatori ? testoHtml(giorno.subappaltatori) : ""}</div></div>` : ""}
    ${giorno.visiteIspettive ? `<div class="section"><div class="section-title">🏛️ Visite ispettive / Direzione Lavori</div><div class="text-area">${giorno.visiteIspettive}</div></div>` : ""}
    ${giorno.sospensioni ? `<div class="section"><div class="section-title">⏸️ Sospensioni lavori</div><div class="text-area">${giorno.sospensioni}</div></div>` : ""}
    ${(giorno.criticita||[]).length ? `<div class="section"><div class="section-title">⚠️ Criticità riscontrate</div>
      ${giorno.criticita.map((c,i)=>{
        const statoLabel = c.stato==="risolta"?"✅ Risolta":c.stato==="in_corso"?"🔵 In corso":"🔴 Aperta";
        return `<div style="border:1px solid #fde68a;border-radius:6px;padding:8px 10px;margin-bottom:6px;font-size:11px;">
          <div style="font-weight:700;margin-bottom:3px;">Criticità ${i+1} — ${statoLabel}</div>
          ${c.descrizione?`<div><strong>Descrizione:</strong> ${c.descrizione}</div>`:""}
          ${c.propostaSoluzione?`<div><strong>Proposta di soluzione:</strong> ${c.propostaSoluzione}</div>`:""}
          ${c.esito?`<div><strong>Esito:</strong> ${c.esito}</div>`:""}
        </div>`;
      }).join("")}
    </div>` : ""}
    ${giorno.annotazioni ? `<div class="section"><div class="section-title">📝 Annotazioni</div><div class="text-area">${giorno.annotazioni}</div></div>` : ""}

    <div class="grid-2" style="margin-top:14px">
      <div class="section">
        <div class="section-title">✍️ Firma Responsabile di Cantiere</div>
        <div class="field-label" style="margin-bottom:6px">${giorno.responsabile||"—"}</div>
        ${giorno.firma ? `<img src="${giorno.firma}" style="max-width:200px;max-height:80px;border:1px solid #e5e7eb;border-radius:6px;padding:4px"/>` : '<div class="firma-box">Firma non presente</div>'}
      </div>
      <div class="section">
        <div class="section-title">📋 Per presa visione</div>
        <div class="firma-box" style="height:80px;font-size:10px;color:#999">Firma DL / Committente</div>
      </div>
    </div>

    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>${project?.name||""} ${project?.codiceCommessa?"· "+project.codiceCommessa:""}</span>
      <span>Stampato il ${new Date().toLocaleDateString("it-IT")}</span>
    </div>

    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  const updForm = (k, v) => setFormGiorno(f => ({ ...f, [k]: v }));
  const giornoAperto = !!formGiorno || vistaMensileAttiva;

  return (
    <div ref={radiceRef} className={`gc-root${giornoAperto ? " gc-aperto" : ""}`} style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      <style>{CSS_TELEFONO}</style>

      {/* Lista giorni */}
      <div className="gc-lista" style={{ width:260, borderRight:"1px solid #e5e7eb", background:"#fff", display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"14px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#1e1b4b" }}>Giornale</div>
          {!commessaChiusa && (
            <button onClick={()=>nuovoGiorno()}
              style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 10px", border:"none", borderRadius:7,
                background:"#7c3aed", color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <Plus size={11}/> Nuovo
            </button>
          )}
        </div>
        <div style={{ flex:1, overflowY:"auto" }}>
          {giorni.length === 0 && (
            <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:12 }}>
              Nessun giorno registrato.<br/>Clicca «Nuovo» per iniziare.
            </div>
          )}
          {giorni.map(g => {
            const d = new Date(g.data+"T00:00:00");
            const sel = giornoSelezionato?.id === g.id;
            return (
              <div key={g.id} onClick={()=>apriGiorno(g)}
                style={{ padding:"10px 14px", borderBottom:"1px solid #f3f4f6", cursor:"pointer",
                  background: sel ? "#ede9fe" : "transparent",
                  borderLeft: sel ? "3px solid #7c3aed" : "3px solid transparent" }}>
                <div style={{ fontSize:12, fontWeight:700, color: sel?"#5b21b6":"#111827" }}>
                  {d.toLocaleDateString("it-IT",{weekday:"short",day:"numeric",month:"short"})}
                </div>
                <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>
                  {g.meteo} {g.responsabile&&`· ${g.responsabile}`}
                </div>
                {g.firma && <div style={{ fontSize:10, color:"#059669", marginTop:2 }}>✓ Firmato</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Area principale */}
      <div className="gc-main" style={{ flex:1, overflowY:"auto", padding:24, background:"#f9fafb" }}>
        {/* giorni iniziati e non salvati su questo dispositivo */}
        {!formGiorno && !commessaChiusa && (
          <ElencoBozze prefisso={prefissoBozze(currentUser?.uid, "giornale", projectId)} documenti={giorni}
            testoDi={(b, g) => g ? `Modifiche non salvate al giorno${delGiorno(g.data)}` : `Giorno del giornale non salvato${delGiorno(b.info?.data)}`}
            onRiprendi={g => {
              if (g) apriGiorno(g); else nuovoGiorno(undefined);
              setRiprendiBozzaGiorno(true);
            }} />
        )}

        {/* Toggle Elenco giorni / Vista Mensile */}
        <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
          <button onClick={()=>setVistaMensileAttiva(false)}
            style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${!vistaMensileAttiva?"#7c3aed":"#e5e7eb"}`,
              background: !vistaMensileAttiva?"#ede9fe":"#fff", color: !vistaMensileAttiva?"#7c3aed":"#6b7280",
              fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            📋 Dettaglio giorno
          </button>
          <button onClick={()=>setVistaMensileAttiva(true)}
            style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${vistaMensileAttiva?"#7c3aed":"#e5e7eb"}`,
              background: vistaMensileAttiva?"#ede9fe":"#fff", color: vistaMensileAttiva?"#7c3aed":"#6b7280",
              fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            📅 Vista Mensile (ore per operaio)
          </button>
        </div>

        {vistaMensileAttiva ? (
          <GiornaleVistaMensile projectId={projectId} orgId={orgId} project={project} datiCommessa={datiCommessa} orgInfo={orgInfo}
            giorni={giorni} dipendenti={dipendenti} currentUser={currentUser} addToast={addToast}
            onApriGiorno={(dateObj)=>{ apriOCreaGiornoData(dateObj); setVistaMensileAttiva(false); }}/>
        ) : (
        <>

        {/* Dati commessa (sul telefono, con un giorno aperto, resta nascosto per dare spazio al modulo) */}
        <div className="gc-card gc-dati" style={cardStyle}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: editDati?16:0 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"#1e1b4b" }}>📋 Dati Commessa / Appalto</div>
            <div style={{ display:"flex", gap:6 }}>
              {!editDati && (
                <button onClick={()=>setEditDati(true)}
                  style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 10px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", fontSize:11, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                  <Edit2 size={11}/> Modifica
                </button>
              )}
              {editDati && (
                <>
                  <button onClick={()=>setEditDati(false)}
                    style={{ padding:"5px 10px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", fontSize:11, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    Annulla
                  </button>
                  <button onClick={salvaDatiCommessa}
                    style={{ padding:"5px 10px", border:"none", borderRadius:7, background:"#7c3aed", color:"#fff", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <Check size={11}/> Salva
                  </button>
                </>
              )}
            </div>
          </div>

          {editDati ? (
            <div>
              <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>
                {/* Appaltante */}
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>APPALTANTE</div>
                  {[{k:"appaltante_nome",l:"Denominazione"},{k:"appaltante_piva",l:"P.IVA / C.F."},{k:"appaltante_indirizzo",l:"Indirizzo"},{k:"appaltante_referente",l:"Referente"}].map(f=>(
                    <div key={f.k} style={{ marginBottom:8 }}>
                      <label style={lbl}>{f.l}</label>
                      <input style={inp} value={formDati[f.k]||""} onChange={e=>setFormDati(p=>({...p,[f.k]:e.target.value}))}/>
                    </div>
                  ))}
                </div>
                {/* Appaltatore */}
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>APPALTATORE</div>
                  {[{k:"appaltatore_nome",l:"Denominazione"},{k:"appaltatore_piva",l:"P.IVA"},{k:"appaltatore_indirizzo",l:"Indirizzo"},{k:"subappaltatrice_nome",l:"Impresa Subappaltatrice"}].map(f=>(
                    <div key={f.k} style={{ marginBottom:8 }}>
                      <label style={lbl}>{f.l}</label>
                      <input style={inp} value={formDati[f.k]||""} onChange={e=>setFormDati(p=>({...p,[f.k]:e.target.value}))}/>
                    </div>
                  ))}
                </div>
                {/* DL */}
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>DIREZIONE LAVORI</div>
                  {[{k:"dl_nome",l:"Nominativo"},{k:"dl_albo",l:"Ordine / Albo"},{k:"dl_recapito",l:"Recapito"}].map(f=>(
                    <div key={f.k} style={{ marginBottom:8 }}>
                      <label style={lbl}>{f.l}</label>
                      <input style={inp} value={formDati[f.k]||""} onChange={e=>setFormDati(p=>({...p,[f.k]:e.target.value}))}/>
                    </div>
                  ))}
                </div>
              </div>
              <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginTop:16 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>PROGETTO ESECUTIVO</div>
                  <div style={{ marginBottom:8 }}>
                    <label style={lbl}>Nominativo</label>
                    <input style={inp} value={formDati.progetto_esecutivo_nome||""} onChange={e=>setFormDati(p=>({...p,progetto_esecutivo_nome:e.target.value}))}/>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>CSE — Coordinatore Sicurezza Esecuzione</div>
                  <div style={{ marginBottom:8 }}>
                    <label style={lbl}>Nominativo</label>
                    <input style={inp} value={formDati.cse_nome||""} onChange={e=>setFormDati(p=>({...p,cse_nome:e.target.value}))}/>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#7c3aed", marginBottom:8 }}>RUP — Responsabile Unico Procedimento</div>
                  <div style={{ marginBottom:8 }}>
                    <label style={lbl}>Nominativo</label>
                    <input style={inp} value={formDati.rup_nome||""} onChange={e=>setFormDati(p=>({...p,rup_nome:e.target.value}))}/>
                  </div>
                </div>
              </div>
              <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr", gap:16, marginTop:16 }}>
                {[{k:"oggetto",l:"Oggetto lavori"},{k:"importo",l:"Importo contrattuale (€)"},{k:"dataConsegna",l:"Data consegna",type:"date"}].map(f=>(
                  <div key={f.k}>
                    <label style={lbl}>{f.l}</label>
                    <input style={inp} type={f.type||"text"} value={formDati[f.k]||""} onChange={e=>setFormDati(p=>({...p,[f.k]:e.target.value}))}/>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="gc-griglia2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
              {[
                { title:"Appaltante", items:[{l:"Denominazione",v:datiCommessa?.appaltante_nome},{l:"P.IVA",v:datiCommessa?.appaltante_piva},{l:"Referente",v:datiCommessa?.appaltante_referente}] },
                { title:"Appaltatore", items:[{l:"Denominazione",v:datiCommessa?.appaltatore_nome},{l:"P.IVA",v:datiCommessa?.appaltatore_piva},{l:"Subappaltatrice",v:datiCommessa?.subappaltatrice_nome}] },
                { title:"Direzione Lavori", items:[{l:"Nominativo",v:datiCommessa?.dl_nome},{l:"Albo",v:datiCommessa?.dl_albo}] },
                { title:"Progetto Esecutivo", items:[{l:"Nominativo",v:datiCommessa?.progetto_esecutivo_nome}] },
                { title:"CSE", items:[{l:"Nominativo",v:datiCommessa?.cse_nome}] },
                { title:"RUP", items:[{l:"Nominativo",v:datiCommessa?.rup_nome}] },
              ].map(s=>(
                <div key={s.title}>
                  <div style={{ fontSize:10, fontWeight:800, color:"#7c3aed", marginBottom:6, textTransform:"uppercase" }}>{s.title}</div>
                  {s.items.map(i=>(
                    <div key={i.l} style={{ marginBottom:4 }}>
                      <span style={{ fontSize:10, color:"#9ca3af" }}>{i.l}: </span>
                      <span style={{ fontSize:11, fontWeight:600, color:"#374151" }}>{i.v||"—"}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form giorno */}
        {(showNuovoGiorno || giornoSelezionato) && formGiorno && (
          <div className="gc-card" style={cardStyle}>
            <button type="button" className="gc-solo-tel" onClick={()=>{ setShowNuovoGiorno(false); setGiornoSelezionato(null); setFormGiorno(null); }}
              style={{ display:"none", alignItems:"center", gap:4, marginBottom:10, padding:"5px 10px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", color:"#5b21b6", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              ← Giorni
            </button>
            <div className="gc-testata" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
              <div style={{ fontSize:14, fontWeight:700, color:"#1e1b4b" }}>
                📅 {new Date(formGiorno.data+"T00:00:00").toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                {commessaChiusa && (
                  <span style={{ fontSize:11, fontWeight:700, color:"#dc2626" }}>🔒 Commessa chiusa — sola consultazione</span>
                )}
                {(giornoSelezionato||showNuovoGiorno) && (
                  <button onClick={()=>stampaPDF(formGiorno)}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", border:"1px solid #2563eb", borderRadius:8, background:"#dbeafe", color:"#1d4ed8", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <Printer size={13}/> Stampa PDF
                  </button>
                )}
                {!commessaChiusa && (
                  <button onClick={salvaGiorno} disabled={loading}
                    style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px", border:"none", borderRadius:8, background:"#7c3aed", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                    <Check size={13}/> {loading?"Salvataggio...":"Salva"}
                  </button>
                )}
                <button onClick={()=>{ setShowNuovoGiorno(false); setGiornoSelezionato(null); setFormGiorno(null); }}
                  style={{ padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff", cursor:"pointer" }}>
                  <X size={13}/>
                </button>
              </div>
            </div>

            {!commessaChiusa && (
              <BozzaGiorno key={`${giornoSelezionato?.id || "nuovo"}-${aperturaGiorno}`} uid={currentUser?.uid} projectId={projectId} nomeCommessa={[project?.codiceCommessa, project?.name].filter(Boolean).join(" ")} giorno={giornoSelezionato}
                formGiorno={formGiorno} setFormGiorno={setFormGiorno} riprendi={riprendiBozzaGiorno} bozzaRef={bozzaGiornoRef} />
            )}

            {/* Data (solo nuovo) */}
            {showNuovoGiorno && (
              <div style={{ marginBottom:16 }}>
                <label style={lbl}>Data *</label>
                <input type="date" style={{...inp, maxWidth:200}} value={formGiorno.data||oggi}
                  onChange={e=>updForm("data",e.target.value)}/>
              </div>
            )}

            <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
              {/* Meteo */}
              <div>
                <label style={lbl}>Condizioni meteo</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {METEO_OPTIONS.map(m=>(
                    <button key={m} onClick={()=>updForm("meteo",m)}
                      style={{ padding:"4px 10px", borderRadius:20, border:`1px solid ${formGiorno.meteo===m?"#7c3aed":"#e5e7eb"}`,
                        background:formGiorno.meteo===m?"#ede9fe":"#fff", color:formGiorno.meteo===m?"#5b21b6":"#374151",
                        fontSize:11, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={lbl}>Temperatura (°C)</label>
                <input style={{...inp, maxWidth:120}} type="number" value={formGiorno.temperatura||""}
                  onChange={e=>updForm("temperatura",e.target.value)} placeholder="Es. 22"/>
              </div>
            </div>

            {/* Manodopera e figure professionali — nominativo e ore lavorate, riga per riga */}
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>⏱️ Manodopera e figure professionali — nominativo e ore lavorate</label>
              {presenzeGiorno.length>0 && (
                <div style={{fontSize:11,color:"#059669",marginBottom:6}}>
                  {presenzeGiorno.length} persone risultano da presenze reali: {presenzeGiorno.join(", ")}
                </div>
              )}
              {(formGiorno.manodopera||[]).map((r,i)=>(
                <div key={r.id} className="gc-riga" style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
                  <select style={{...inp,flex:1}} value={r.dipendenteId||""}
                    onChange={e=>{
                      const dip = dipendenti.find(d=>d.id===e.target.value);
                      const righe=[...(formGiorno.manodopera||[])];
                      righe[i]={...r,dipendenteId:e.target.value,nome:dip?`${dip.nome||""} ${dip.cognome||""}`.trim():r.nome};
                      updForm("manodopera",righe);
                    }}>
                    <option value="">— Seleziona dipendente —</option>
                    {dipendenti.map(d=><option key={d.id} value={d.id}>{d.nome} {d.cognome}</option>)}
                  </select>
                  <input type="text" style={{...inp,width:160}} value={r.nome||""} placeholder="Oppure nome libero"
                    onChange={e=>{ const righe=[...(formGiorno.manodopera||[])]; righe[i]={...r,nome:e.target.value,dipendenteId:""}; updForm("manodopera",righe); }}/>
                  <input type="number" min="0" step="0.5" style={{...inp,width:80}} value={r.ore||""} placeholder="Ore"
                    onChange={e=>{ const righe=[...(formGiorno.manodopera||[])]; righe[i]={...r,ore:e.target.value}; updForm("manodopera",righe); }}/>
                  <button type="button" onClick={()=>updForm("manodopera",(formGiorno.manodopera||[]).filter((_,idx)=>idx!==i))}
                    style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer"}}><Trash2 size={14}/></button>
                </div>
              ))}
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button type="button" onClick={()=>updForm("manodopera",[...(formGiorno.manodopera||[]),{id:Date.now().toString(),dipendenteId:"",nome:"",ore:""}])}
                  style={{fontSize:12,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>+ Aggiungi operaio/figura professionale</button>
                {presenzeGiorno.length>0 && (formGiorno.manodopera||[]).length===0 && (
                  <button type="button" onClick={()=>updForm("manodopera",presenzeGiorno.map(nome=>({id:Date.now().toString()+Math.random(),dipendenteId:"",nome,ore:""})))}
                    style={{fontSize:12,color:"#059669",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>↺ Precompila da presenze del giorno</button>
                )}
              </div>
            </div>

            {/* Lavori eseguiti */}
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>
                🔧 Lavori eseguiti
                {rapportiniGiorno.length>0 && <span style={{fontWeight:400,marginLeft:6,color:"#059669"}}>({rapportiniGiorno.length} rapportini disponibili)</span>}
              </label>
              <textarea rows={3} style={{...inp,resize:"vertical"}} value={formGiorno.lavorieseguiti||""}
                onChange={e=>updForm("lavorieseguiti",e.target.value)}
                placeholder="Descrizione dettagliata dei lavori eseguiti oggi..."/>
            </div>

            <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
              <div>
                <label style={lbl}>📦 Materiali e forniture ricevute</label>
                <textarea rows={2} style={{...inp,resize:"vertical"}} value={formGiorno.materialiRicevuti||""}
                  onChange={e=>updForm("materialiRicevuti",e.target.value)} placeholder="Materiali consegnati in cantiere oggi..."/>
              </div>
              <div>
                <label style={lbl}>🚧 Attrezzature e mezzi</label>
                <textarea rows={2} style={{...inp,resize:"vertical"}} value={formGiorno.attrezzature||""}
                  onChange={e=>updForm("attrezzature",e.target.value)} placeholder="Attrezzature e mezzi presenti..."/>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap:16, marginBottom:16 }}>
              <div>
                <label style={lbl}>🤝 Ditte esterne presenti</label>
                <DittePresentiGiornale value={formGiorno.ditteEsterne||[]} onChange={v=>updForm("ditteEsterne",v)}
                  project={project} orgId={orgId} disabled={commessaChiusa} idoneita={idoneita}
                  testoVecchio={formGiorno.subappaltatori||""} onTestoVecchio={v=>updForm("subappaltatori",v)}/>
              </div>
              <div>
                <label style={lbl}>🏛️ Visite ispettive / Direzione Lavori</label>
                <textarea rows={2} style={{...inp,resize:"vertical"}} value={formGiorno.visiteIspettive||""}
                  onChange={e=>updForm("visiteIspettive",e.target.value)} placeholder="Visite, ispezioni, ordini della DL..."/>
              </div>
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={lbl}>⏸️ Sospensioni lavori</label>
              <textarea rows={2} style={{...inp,resize:"vertical"}} value={formGiorno.sospensioni||""}
                onChange={e=>updForm("sospensioni",e.target.value)} placeholder="Eventuali sospensioni con motivazione..."/>
            </div>

            {/* Criticità — descrizione, proposta di soluzione, esito */}
            <div style={{ marginBottom:16 }}>
              <label style={lbl}>⚠️ Criticità riscontrate</label>
              {(formGiorno.criticita||[]).map((c,i)=>(
                <div key={c.id} style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:12,marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <span style={{fontSize:11,fontWeight:700,color:"#92400e"}}>Criticità {i+1}</span>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <select value={c.stato||"aperta"} style={{fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:8,border:"none",cursor:"pointer",
                          background: c.stato==="risolta"?"#d1fae5":c.stato==="in_corso"?"#dbeafe":"#fee2e2",
                          color: c.stato==="risolta"?"#065f46":c.stato==="in_corso"?"#1d4ed8":"#991b1b"}}
                        onChange={e=>{ const righe=[...(formGiorno.criticita||[])]; righe[i]={...c,stato:e.target.value}; updForm("criticita",righe); }}>
                        <option value="aperta">Aperta</option>
                        <option value="in_corso">In corso</option>
                        <option value="risolta">Risolta</option>
                      </select>
                      <button type="button" onClick={()=>updForm("criticita",(formGiorno.criticita||[]).filter((_,idx)=>idx!==i))}
                        style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer"}}><Trash2 size={14}/></button>
                    </div>
                  </div>
                  <textarea rows={2} style={{...inp,width:"100%",resize:"vertical",marginBottom:6}} value={c.descrizione||""}
                    placeholder="Descrizione della criticità riscontrata..."
                    onChange={e=>{ const righe=[...(formGiorno.criticita||[])]; righe[i]={...c,descrizione:e.target.value}; updForm("criticita",righe); }}/>
                  <textarea rows={2} style={{...inp,width:"100%",resize:"vertical",marginBottom:6}} value={c.propostaSoluzione||""}
                    placeholder="Proposta di soluzione..."
                    onChange={e=>{ const righe=[...(formGiorno.criticita||[])]; righe[i]={...c,propostaSoluzione:e.target.value}; updForm("criticita",righe); }}/>
                  <textarea rows={2} style={{...inp,width:"100%",resize:"vertical"}} value={c.esito||""}
                    placeholder="Esito della soluzione adottata..."
                    onChange={e=>{ const righe=[...(formGiorno.criticita||[])]; righe[i]={...c,esito:e.target.value}; updForm("criticita",righe); }}/>
                </div>
              ))}
              <button type="button" onClick={()=>updForm("criticita",[...(formGiorno.criticita||[]),{id:Date.now().toString()+Math.random(),descrizione:"",propostaSoluzione:"",esito:"",stato:"aperta"}])}
                style={{fontSize:12,color:"#d97706",background:"none",border:"none",cursor:"pointer",fontWeight:600}}>+ Aggiungi criticità</button>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={lbl}>📝 Annotazioni generali</label>
              <textarea rows={2} style={{...inp,resize:"vertical"}} value={formGiorno.annotazioni||""}
                onChange={e=>updForm("annotazioni",e.target.value)} placeholder="Altre note e annotazioni..."/>
            </div>

            {/* Firma */}
            <div className="gc-griglia" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <div>
                <label style={lbl}>Responsabile di cantiere</label>
                <input style={inp} value={formGiorno.responsabile||""} onChange={e=>updForm("responsabile",e.target.value)}
                  placeholder="Nome e cognome responsabile"/>
              </div>
              <FirmaDigitale conMiaFirma label="Firma del responsabile" value={formGiorno.firma}
                onChange={v=>updForm("firma",v)}/>
            </div>
          </div>
        )}

        {!showNuovoGiorno && !giornoSelezionato && giorni.length === 0 && (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📓</div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>Giornale di cantiere vuoto</div>
            <div style={{ fontSize:12, marginBottom:16 }}>Inizia compilando i dati del contratto e poi aggiungi il primo giorno.</div>
            {!commessaChiusa && (
              <button onClick={()=>nuovoGiorno()}
                style={{ padding:"8px 20px", border:"none", borderRadius:8, background:"#7c3aed", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                + Aggiungi primo giorno
              </button>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
